import { fetchAPI } from "./utils";

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
    return localStorage.getItem("token");
};

export interface CreateBrandVoiceRequest {
    name: string;
    language: "en" | "fr"; // Required: specifies which language to generate description in
    text?: string;
    websiteText?: string;
    fileUrls?: string[];
    companyFileIds?: string[];
}

export interface BrandVoice {
    id: string;
    name: string;
    description: string;
    excerpts: string[];
    companyId: string;
    createdAt: string;
    updatedAt: string;
}

export interface UpdateBrandVoiceRequest {
    brandVoiceId: string;
    name?: string;
    description?: string;
    excerpts?: string[];
    deleteExcerptIndex?: number;
}

export interface DeleteBrandVoiceRequest {
    brandVoiceId: string;
}

/**
 * Create a new BrandVoice
 */
export const createBrandVoice = async (data: CreateBrandVoiceRequest): Promise<BrandVoice> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<BrandVoice>("/create-brand-voice", "POST", data, token);
};

/**
 * Get all BrandVoices for the company, or a specific one by ID
 */
export const getBrandVoices = async (brandVoiceId?: string): Promise<BrandVoice[]> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    const endpoint = brandVoiceId ? `/get-brand-voices?brandVoiceId=${brandVoiceId}` : "/get-brand-voices";
    const response = await fetchAPI<BrandVoice | BrandVoice[]>(endpoint, "GET", undefined, token);
    // Always return an array
    return Array.isArray(response) ? response : [response];
};

/**
 * Get a single BrandVoice by ID
 */
export const getBrandVoice = async (brandVoiceId: string): Promise<BrandVoice> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    const endpoint = `/get-brand-voices?brandVoiceId=${brandVoiceId}`;
    const response = await fetchAPI<BrandVoice | BrandVoice[]>(endpoint, "GET", undefined, token);
    // If it's an array, return the first item, otherwise return the response
    return Array.isArray(response) ? response[0] : response;
};

/**
 * Update a BrandVoice
 */
export const updateBrandVoice = async (data: UpdateBrandVoiceRequest): Promise<BrandVoice> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<BrandVoice>("/update-brand-voice", "POST", data, token);
};

/**
 * Delete a BrandVoice
 */
export const deleteBrandVoice = async (brandVoiceId: string): Promise<{ message: string }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<{ message: string }>("/delete-brand-voice", "POST", { brandVoiceId }, token);
};
